import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth } from '@/api/ApiCalls';
import { StandingOrderActionResult, describeActionError } from '@/pages/Donation/StandingOrders/standingOrderActions';
import { formatDate, formatRowMoney } from '@/pages/Donation/StandingOrders/standingOrderModel';
import StopReasonField from '@/pages/Donation/StandingOrders/OrderActions/StopReasonField';
import chargeStyles from '@/pages/Donation/StandingOrders/AdditionalInfo/StandingOrderChargeDialog.module.css';
import actionStyles from '@/pages/Donation/StandingOrders/OrderActions/OrderActions.module.css';
import { BulkActionKind, BulkOutcome, DonorOrder, fundLabel, planBulk, rowKey, runBulk } from './donorStandingOrders';
import styles from './DonorStandingOrders.module.css';

interface BulkOrderActionDialogProps {
  kind: BulkActionKind;
  /** The orders the office selected - the dialog sorts out which the action applies to. */
  orders: DonorOrder[];
  donorName: string;
  /** The locked collection that holds an order (the Masav lock, HAT-1441), or null - as the row menu reads it. */
  lockOf: (order: DonorOrder) => Date | null;
  onClose: () => void;
  /** Every order the action went through on, with the server's answer for each. Called once, when the run is over. */
  onDone: (kind: BulkActionKind, outcomes: BulkOutcome[]) => void;
}

/**
 * Freezing, reactivating or cancelling several of a donor's bank orders in one go (HAT-10024: «לסדר
 * את כולן ביחד»). The words are the single-order dialogs' (HAT-1441), said of several; the reason,
 * when given, goes into each order's «מצב הוראה» as it would from the board.
 *
 * One window from start to end, at one size (Chaya, 25.10.2026: a dialog does not grow and shrink
 * with clicks): the orders stay listed where they are and each one's state fills in beside it as it
 * is written, the result takes the line the note was on, and the buttons stay at the bottom. The
 * orders are written one after another, so a refusal on one (changed in the office meanwhile) shows
 * beside the ones that went through. Once started it cannot be stopped: a write already in APT is
 * not taken back by closing the window.
 */
const BulkOrderActionDialog = ({ kind, orders, donorName, lockOf, onClose, onDone }: BulkOrderActionDialogProps) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuthContext();
  const prefix = `donorStandingOrders.bulk.${kind}`;
  const plan = useMemo(() => planBulk(orders, kind, lockOf), [orders, kind, lockOf]);
  const askReason = kind !== 'reactivate';
  const [reason, setReason] = useState('');
  const [outcomes, setOutcomes] = useState<BulkOutcome[] | null>(null);
  const [finished, setFinished] = useState(false);

  const started = outcomes != null;
  const running = started && !finished;
  const failed = outcomes?.filter((o) => o.status === 'failed').length ?? 0;
  const outcomeOf = (order: DonorOrder) => outcomes?.find((o) => rowKey(o.order) === rowKey(order));

  const describe = (order: DonorOrder) =>
    t('donorStandingOrders.orderLine', {
      fund: fundLabel(order, t),
      amount: formatRowMoney(order.amount, order, i18n.language),
    });

  const run = async () => {
    const headers = { Authorization: `Bearer ${currentUser?.token}` };
    setOutcomes(plan.eligible.map((order) => ({ order, status: 'waiting' })));
    const done = await runBulk(
      plan.eligible,
      kind,
      reason,
      (url, body) => postAuth<StandingOrderActionResult, object>(url, headers, body),
      (error) => describeActionError(error, t, true),
      (result) => t('standingOrders.orderActions.errors.nedarimRefused', { message: result.message ?? '' }),
      setOutcomes,
    );
    setFinished(true);
    onDone(kind, done);
  };

  // The line under the list: what will happen, then what did.
  const resultLine = !finished
    ? <p className={actionStyles.hint}>{plan.eligible.length > 0 ? t('donorStandingOrders.bulk.runNote') : ''}</p>
    : (
      <div role="status" className={failed > 0 ? chargeStyles.errorMessage : chargeStyles.successMessage}>
        {failed > 0
          ? t('donorStandingOrders.bulk.doneWithFailures', { count: failed })
          : t(`${prefix}.done`, { count: outcomes?.length ?? 0 })}
      </div>
    );

  return (
    <MainDialogLayout
      open
      // Nothing is undone by closing mid-run, so the window stays until the run is over.
      onOpenChange={(open: boolean) => { if (!open && !running) onClose(); }}
      title={t(`${prefix}.title`, { count: plan.eligible.length, name: donorName })}
      maxWidth="640px"
      contentClassName={`${chargeStyles.compactDialog} ${styles.bulkDialog}`}
    >
      <div className={styles.bulkBody}>
        {kind === 'cancel' ? (
          <div className={actionStyles.dangerBox}>
            <p className={actionStyles.dangerText}>{t(`${prefix}.message`)}</p>
          </div>
        ) : (
          <p className={styles.dialogMessage}>{t(`${prefix}.message`)}</p>
        )}

        <div className={styles.bulkList}>
          {plan.eligible.length > 0 && (
            <ul className={styles.outcomeList} aria-label={t('donorStandingOrders.bulk.eligibleList')} aria-live="polite">
              {plan.eligible.map((order) => {
                const outcome = outcomeOf(order);
                return (
                  <li key={rowKey(order)} data-status={outcome?.status ?? 'ready'}>
                    <span className={styles.outcomeOrder}>{describe(order)}</span>
                    <span className={styles.outcomeStatus}>
                      {outcome ? t(`donorStandingOrders.bulk.row.${outcome.status}`) : ''}
                    </span>
                    {outcome?.error && <span role="alert" className={styles.outcomeError}>{outcome.error}</span>}
                  </li>
                );
              })}
            </ul>
          )}

          {plan.locked.length > 0 && (
            <div className={styles.skipped}>
              <p>{t('donorStandingOrders.bulk.locked', { count: plan.locked.length })}</p>
              <ul className={styles.orderList}>
                {plan.locked.map(({ order, until }) => (
                  <li key={rowKey(order)}>
                    {describe(order)} · {t('standingOrders.orderActions.bank.locked.hint', { date: formatDate(until, i18n.language) })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {plan.skipped.length > 0 && (
            <div className={styles.skipped}>
              <p>{t(`${prefix}.skipped`, { count: plan.skipped.length })}</p>
              <ul className={styles.orderList}>
                {plan.skipped.map((order) => <li key={rowKey(order)}>{describe(order)}</li>)}
              </ul>
            </div>
          )}
        </div>

        {askReason && plan.eligible.length > 0 && (
          <StopReasonField id={`bulk-${kind}-reason`} value={reason} onChange={setReason} disabled={started} />
        )}

        {resultLine}

        <div className={chargeStyles.actions}>
          {finished ? (
            <Button variant="primary" onClick={onClose}>{t('donorStandingOrders.bulk.close')}</Button>
          ) : (
            <>
              <Button variant="primary" disabled={plan.eligible.length === 0 || running} onClick={run}>
                {running ? t('donorStandingOrders.bulk.running') : t(`${prefix}.confirm`, { count: plan.eligible.length })}
              </Button>
              <button type="button" className={actionStyles.linkButton} onClick={onClose} disabled={running}>
                {t('donorStandingOrders.bulk.back')}
              </button>
            </>
          )}
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default BulkOrderActionDialog;
